import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { StepType } from './StepType';
import { UploadImage } from './UploadImage';
import { ReleaseInfo } from './ReleaseInfo';
import { TrackUploadInfo } from './TrackUploadInfo';
import { Publish } from './Publish';
import { useRelease } from "./hooks/useRelease";
import { Platform } from "components/NewRelease/Platform";
import { Step } from "./constants";
import { DeviceSize } from "constants/deviceSize";

export const NewRelease: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const stepType = queryParams.get("step") as Step | null;
  const currentStep: Step = (stepType || Step.Type) as Step;
  const { release, fetching, fetchRelease } = useRelease(id);

  useEffect(() => {
    // Ensure the URL always has a step (prevents "empty screen" on first paint)
    if (!stepType) {
      const path = id ? `/releases/new-release/${id}` : "/releases/new-release";
      const query = new URLSearchParams({ step: Step.Type }).toString();
      const hash = location.hash || "";
      navigate(`${path}?${query}${hash}`, { replace: true });
    }
  }, [stepType, id, location.hash, navigate]);

  useEffect(() => {
    // IMPORTANT: do NOT call updateRelease(id) here (it POSTs and can be very slow + can overwrite data).
    // Just fetch the release once we have an id, so header/cover info is available.
    if (!id) return;
    fetchRelease();
  }, [id, fetchRelease]);

  useEffect(() => {
    // On mobile, when switching between steps (1→2, 2→3, 3→4/5, etc.), always scroll to top.
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const tabletPx = parseInt(DeviceSize.TabletMedium, 10) || 768;
    const isMobile = window.innerWidth <= tabletPx;
    if (!isMobile) return;

    // Scroll main viewport
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // Additionally reset scroll for common roots (Safari quirks / nested layouts)
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }

    // And reset MainLayout scroll container
    const main = document.getElementById("main-scroll-container");
    if (main) {
      main.scrollTop = 0;
    }
  }, [currentStep]);

  const content = useMemo(() => {
    switch (currentStep) {
      case Step.ReleaseInfo:
        return <ReleaseInfo />;
      case Step.UploadImage:
        return <UploadImage release={release} />;
      case Step.TrackUpload:
      case Step.TrackInfo:
        return <TrackUploadInfo release={release} fetching={fetching} />;
      case Step.Publish:
        return <Publish release={release} />;
      case Step.Platform:
      case Step.paidPlatform:
        return <Platform />;
      case Step.Type:
      default:
        return <StepType />;
    }
  }, [currentStep, release, fetching]);

  return content;
};

